import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "../data/services";

/**
 * "Continue Exploring" — shows the sibling service landing pages so a visitor
 * on one service naturally discovers the others.
 *
 * Props:
 *   currentSlug — slug of the page it's rendered on; that entry is filtered out.
 */
const RelatedServices = ({ currentSlug }) => {
  const others = SERVICES.filter((s) => s.slug !== currentSlug);
  if (others.length === 0) return null;

  return (
    <section
      id="related-services"
      data-testid="related-services"
      className="relative border-t border-white/5 bg-ink-deep/40"
    >
      <div className="max-w-[1180px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10 lg:mb-12">
          <div>
            <div className="text-[10.5px] tracking-[0.34em] uppercase text-gold">
              Continue Exploring
            </div>
            <h2 className="mt-4 font-serif text-white text-[24px] sm:text-[28px] lg:text-[32px] leading-[1.1] tracking-[-0.012em]">
              Related{" "}
              <span className="italic text-azure-soft">services</span>.
            </h2>
            <div className="mt-6 h-px w-12 bg-azure-soft/60" />
          </div>
          <Link
            to="/consulting"
            data-testid="related-services-view-all"
            className="group inline-flex items-center gap-3 text-[10.5px] tracking-[0.34em] uppercase text-white/60 hover:text-azure-soft transition-colors"
          >
            View all initiatives
            <ArrowUpRight
              className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
              strokeWidth={1.6}
            />
          </Link>
        </div>

        <div className="grid md:grid-cols-2 gap-5 lg:gap-6">
          {others.map((s) => (
            <Link
              key={s.slug}
              to={s.href}
              data-testid={`related-service-${s.slug}`}
              className="group relative flex flex-col border border-white/10 hover:border-azure/40 bg-ink-soft/40 p-7 lg:p-8 transition-colors duration-500"
            >
              <span
                aria-hidden="true"
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse 80% 100% at 0% 0%, rgba(74,143,229,0.10), transparent 70%)",
                }}
              />
              <div className="relative flex flex-col h-full">
                <div className="flex items-baseline gap-4">
                  <span className="font-serif italic text-azure-soft text-[18px] leading-none">
                    {s.n}
                  </span>
                  <span className="h-px w-8 bg-azure-soft/40" />
                </div>
                <h3 className="mt-5 font-serif text-white text-[20px] lg:text-[22px] leading-[1.2] tracking-[-0.012em] group-hover:text-azure-soft transition-colors duration-500">
                  {s.title}
                </h3>
                <p className="mt-4 text-white/65 text-[14px] leading-[1.85] max-w-[45ch]">
                  {s.tagline}
                </p>
                <div className="mt-auto pt-7 inline-flex items-center gap-3 text-[10.5px] tracking-[0.34em] uppercase text-white/55 group-hover:text-azure-soft transition-colors duration-500">
                  Learn More
                  <ArrowUpRight
                    className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                    strokeWidth={1.6}
                  />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedServices;
